import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Users, ArrowRight, CheckCircle, Network, BarChart3, 
  Shield, MessageSquare, Star, ChevronRight, Menu, X,
  Phone, MapPin, Mail, Send, Linkedin, Facebook, Instagram, Play
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import MarketSelect from "../components/MarketSelect";
import { getStoredMarket, setStoredMarket } from "../constants/markets";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const LandingPage = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedMarket, setSelectedMarket] = useState(getStoredMarket());
  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: ""
  });
  const [submitting, setSubmitting] = useState(false);

  const handleMarketChange = (v) => {
    setSelectedMarket(v);
    setStoredMarket(v);
  };

  const handleBrowseProviders = () => {
    if (selectedMarket) {
      navigate(`/browse?market=${encodeURIComponent(selectedMarket)}`);
    } else {
      navigate("/browse");
    }
  };

  const features = [
    {
      icon: Users,
      title: "Provider Directory",
      description: "Browse our nationwide network of physicians, clinicians, and healthcare professionals."
    },
    {
      icon: Network,
      title: "Schedule Meetings",
      description: "Book lunches, breakfasts, coffee meetings, and in-services directly with providers."
    },
    {
      icon: MessageSquare,
      title: "Direct Messaging",
      description: "Connect and communicate directly with healthcare professionals through our secure platform."
    },
    {
      icon: BarChart3,
      title: "iConcierge Assistance",
      description: "Get help with introductions, marketing descriptions, and partnership facilitation."
    }
  ];

  const [billingCycle, setBillingCycle] = useState("monthly");

  const tiers = {
    monthly: {
      name: "Monthly",
      price: "$29.99",
      period: "/mo",
      plan_id: "monthly",
      description: "Flexible month-to-month access",
      features: [
        "Full provider directory access",
        "Schedule lunches & meetings",
        "In-app messaging",
        "Meeting scheduling tools",
        "Email notifications"
      ],
      cta: "Subscribe Monthly",
      popular: false,
      savings: null
    },
    quarterly: {
      name: "Quarterly",
      price: "$59.99",
      period: "/qtr",
      plan_id: "quarterly",
      description: "Save 33% with quarterly billing",
      features: [
        "Full provider directory access",
        "Schedule lunches & meetings",
        "In-app messaging",
        "Meeting scheduling tools",
        "Email notifications",
        "Analytics dashboard"
      ],
      cta: "Subscribe Quarterly",
      popular: true,
      savings: "Save 33%"
    },
    annual: {
      name: "Annual",
      price: "$199.99",
      period: "/yr",
      plan_id: "annual",
      description: "Best value — save 44%",
      features: [
        "Full provider directory access",
        "Schedule lunches & meetings",
        "In-app messaging",
        "Meeting scheduling tools",
        "Email notifications",
        "Analytics dashboard",
        "Priority support"
      ],
      cta: "Subscribe Annually",
      popular: false,
      savings: "Save 31%"
    }
  };

  const marketingTier = {
    name: "Marketing Services",
    price: "Custom",
    period: "",
    description: "Tiered pricing packages tailored to your practice's unique needs and growth goals",
    features: [
      "Dedicated account manager",
      "Strategic marketing & execution",
      "Business development",
      "Custom campaign management",
      "Growth analytics & reporting"
    ],
    cta: "Contact Us",
    isEnterprise: true
  };

  const handleCheckout = async (planId) => {
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(`${API}/membership/checkout`, {
        plan_id: planId,
        origin_url: originUrl,
      });
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error("Unable to start checkout. Please try again.");
    }
  };

  const testimonials = [
    {
      quote: "MedConnect transformed how we receive referrals. We've seen a 40% increase in quality patient leads.",
      author: "Dr. Sarah Chen",
      role: "Cardiologist",
      avatar: "https://images.pexels.com/photos/6749773/pexels-photo-6749773.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=100&w=100"
    },
    {
      quote: "The network has connected me with specialists I trust. My patients get better care, faster.",
      author: "Dr. Michael Rodriguez",
      role: "Family Medicine",
      avatar: "https://images.pexels.com/photos/6762862/pexels-photo-6762862.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=100&w=100"
    }
  ];

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await axios.post(`${API}/public/contact`, {
        name: contactForm.name,
        email: contactForm.email,
        phone: contactForm.phone,
        message: contactForm.message
      });
      toast.success("Thank you! We'll be in touch soon.");
      setContactForm({ name: "", email: "", phone: "", message: "" });
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glassmorphism">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_medical-referral-hub/artifacts/m1ei4qxx_IMG_5363.jpeg" 
                alt="iConcierge Logo" 
                className="h-10 w-auto"
              />
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-8">
              <Link to="/browse" className="text-slate-600 hover:text-primary transition-colors">Browse Providers</Link>
              <Link to="/tour" className="text-slate-600 hover:text-primary transition-colors">Platform Tour</Link>
              <a href="#pricing" className="text-slate-600 hover:text-primary transition-colors">Pricing</a>
              <a href="#contact" className="text-slate-600 hover:text-primary transition-colors">Contact</a>
            </nav>

            <div className="hidden md:flex items-center gap-3">
              <Button 
                variant="ghost" 
                onClick={() => navigate("/login")}
                data-testid="header-login-btn"
              >
                Sign In
              </Button>
              <Button 
                onClick={() => navigate("/register")}
                className="bg-primary text-white hover:bg-primary/90 rounded-full"
                data-testid="header-register-btn"
              >
                Get Started
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="md:hidden bg-white border-t border-slate-200 py-4 px-4"
          >
            <nav className="flex flex-col gap-4">
              <Link to="/browse" className="text-slate-600 py-2">Browse Providers</Link>
              <a href="#features" className="text-slate-600 py-2">Features</a>
              <a href="#pricing" className="text-slate-600 py-2">Pricing</a>
              <a href="#contact" className="text-slate-600 py-2">Contact</a>
              <hr className="border-slate-200" />
              <Button variant="outline" onClick={() => navigate("/login")} className="w-full">
                Sign In
              </Button>
              <Button onClick={() => navigate("/register")} className="w-full bg-primary text-white">
                Get Started
              </Button>
            </nav>
          </motion.div>
        )}
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="inline-flex items-center gap-2 bg-blue-50 text-primary px-4 py-1.5 rounded-full text-sm font-medium mb-6">
                <Star className="w-4 h-4" />
                Trusted by physicians nationwide
              </div>
              
              <h1 className="text-5xl md:text-6xl font-bold text-slate-900 leading-tight mb-6" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                <span style={{ fontFamily: 'Orbitron, sans-serif' }} className="text-gradient">iConcierge</span>
                <br />
                <span className="text-3xl md:text-4xl font-semibold text-slate-700">Healthcare Marketing</span>
              </h1>
              
              <p className="text-lg md:text-xl text-slate-600 mb-8 leading-relaxed">
                The Healthcare Growth Network — built for physicians, reps, and clinic teams nationwide.
                We don&apos;t just get you patients—we get you the right patients through trusted providers.
              </p>

              <div className="flex flex-col sm:flex-row gap-3 mb-6">
                <div className="flex-1 max-w-xs" data-testid="hero-market-wrapper">
                  <MarketSelect
                    value={selectedMarket}
                    onChange={handleMarketChange}
                    variant="hero"
                    testId="hero-market-select"
                    placeholder="Choose your market"
                  />
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg"
                  onClick={handleBrowseProviders}
                  className="bg-primary text-white hover:bg-primary/90 rounded-full px-8 text-base"
                  data-testid="hero-cta-btn"
                >
                  Browse Providers
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  onClick={() => navigate("/tour")}
                  className="rounded-full px-8 text-base"
                >
                  <Play className="w-4 h-4 mr-2" />
                  Watch Tour
                </Button>
              </div>

              <div className="flex items-center gap-6 mt-8 pt-8 border-t border-slate-200">
                <div>
                  <div className="text-2xl font-bold text-slate-900">2,500+</div>
                  <div className="text-sm text-slate-500">Referrals Monthly</div>
                </div>
                <div className="w-px h-12 bg-slate-200"></div>
                <div>
                  <div className="text-2xl font-bold text-slate-900">94%</div>
                  <div className="text-sm text-slate-500">Acceptance Rate</div>
                </div>
                <div className="w-px h-12 bg-slate-200"></div>
                <div>
                  <div className="text-2xl font-bold text-slate-900">48hrs</div>
                  <div className="text-sm text-slate-500">Avg Response Time</div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="relative"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1659353888477-6e6aab941b55?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
                  alt="Doctor using tablet in modern clinic"
                  className="w-full h-auto"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/20 to-transparent"></div>
              </div>
              
              {/* Floating Card */}
              <motion.div 
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 }}
                className="absolute -left-8 bottom-12 bg-white rounded-xl shadow-lg p-4 border border-slate-100"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <div className="font-semibold text-slate-900">New Referral</div>
                    <div className="text-sm text-slate-500">From Dr. Smith • Just now</div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              Everything You Need to Grow
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Built for physicians who want results, not complexity. Simple tools that drive real growth.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-slate-50 rounded-xl p-6 hover:shadow-md transition-shadow duration-300"
              >
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{feature.title}</h3>
                <p className="text-slate-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              Platform Membership
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-4">
              For reps, clinicians, and healthcare businesses to schedule meetings and access the directory.
            </p>
            <div className="inline-flex items-center bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-medium mb-8">
              Start your 30-day free trial — no credit card required
            </div>

            {/* Billing Toggle */}
            <div className="inline-flex items-center bg-slate-100 rounded-full p-1 gap-1" data-testid="billing-toggle">
              {["monthly", "quarterly", "annual"].map((cycle) => (
                <button
                  key={cycle}
                  onClick={() => setBillingCycle(cycle)}
                  className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                    billingCycle === cycle
                      ? "bg-primary text-white shadow-sm"
                      : "text-slate-600 hover:text-slate-900"
                  }`}
                  data-testid={`billing-${cycle}-btn`}
                >
                  {cycle.charAt(0).toUpperCase() + cycle.slice(1)}
                </button>
              ))}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto mb-16">
            {/* Active Plan Card */}
            {(() => {
              const tier = tiers[billingCycle];
              return (
                <motion.div
                  key={billingCycle}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white rounded-2xl p-8 border-2 border-primary shadow-xl"
                >
                  {tier.savings && (
                    <div className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-700 text-sm font-medium px-3 py-1 rounded-full mb-4">
                      {tier.savings}
                    </div>
                  )}
                  <h3 className="text-xl font-bold text-slate-900 mb-2">{tier.name}</h3>
                  <p className="text-slate-600 mb-4">{tier.description}</p>
                  <div className="flex items-baseline gap-1 mb-6">
                    <span className="text-4xl font-bold text-slate-900">{tier.price}</span>
                    <span className="text-slate-500">{tier.period}</span>
                  </div>
                  <ul className="space-y-3 mb-8">
                    {tier.features.map((feature) => (
                      <li key={feature} className="flex items-center gap-2 text-slate-600">
                        <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <Button 
                    className="w-full rounded-full bg-primary text-white hover:bg-primary/90"
                    onClick={() => navigate("/register")}
                    data-testid={`checkout-${tier.plan_id}-btn`}
                  >
                    Start Free Trial
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </motion.div>
              );
            })()}

            {/* Marketing Services Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-8 border-2 border-slate-200 hover:border-slate-300 transition-all"
            >
              <div className="inline-flex items-center gap-1 bg-primary/10 text-primary text-sm font-medium px-3 py-1 rounded-full mb-4">
                Full Service
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">{marketingTier.name}</h3>
              <p className="text-slate-600 mb-4">{marketingTier.description}</p>
              <div className="flex items-baseline gap-1 mb-6">
                <span className="text-4xl font-bold text-slate-900">{marketingTier.price}</span>
                <span className="text-slate-500">{marketingTier.period}</span>
              </div>
              <ul className="space-y-3 mb-8">
                {marketingTier.features.map((feature) => (
                  <li key={feature} className="flex items-center gap-2 text-slate-600">
                    <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>
              <Button 
                className="w-full rounded-full bg-slate-100 text-slate-900 hover:bg-slate-200"
                onClick={() => window.location.href = 'mailto:contact@iconciergehm.com?subject=Marketing%20Services%20Inquiry'}
                data-testid="pricing-marketing-btn"
              >
                {marketingTier.cta}
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              Trusted by Physicians
            </h2>
            <p className="text-lg text-slate-600">See what our members are saying</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.author}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-slate-50 rounded-2xl p-8"
              >
                <p className="text-lg text-slate-700 mb-6 italic">"{testimonial.quote}"</p>
                <div className="flex items-center gap-4">
                  <img 
                    src={testimonial.avatar} 
                    alt={testimonial.author}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div>
                    <div className="font-semibold text-slate-900">{testimonial.author}</div>
                    <div className="text-sm text-slate-500">{testimonial.role}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="contact" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                Let's Talk Growth
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                Ready to transform your practice? Fill out the form and our team will reach out within 24 hours to discuss how iConcierge can help you grow.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Phone className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">Call Us</p>
                    <a href="tel:8882509421" className="text-slate-600 hover:text-primary">(888) 250-9421</a>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Mail className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">Email Us</p>
                    <a href="mailto:contact@iconciergehm.com" className="text-slate-600 hover:text-primary">contact@iconciergehm.com</a>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                    <MapPin className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">Visit Us</p>
                    <p className="text-slate-600">2931 Ridge Rd, Ste 101<br />Rockwall, TX 75032</p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <form onSubmit={handleContactSubmit} className="bg-slate-50 rounded-2xl p-8">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Full Name *</label>
                    <Input
                      value={contactForm.name}
                      onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                      placeholder="Dr. Jane Smith"
                      required
                      className="h-12"
                      data-testid="contact-name"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Email *</label>
                      <Input
                        type="email"
                        value={contactForm.email}
                        onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                        placeholder="jane@clinic.com"
                        required
                        className="h-12"
                        data-testid="contact-email"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
                      <Input
                        value={contactForm.phone}
                        onChange={(e) => setContactForm({...contactForm, phone: e.target.value})}
                        placeholder="(555) 123-4567"
                        className="h-12"
                        data-testid="contact-phone"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">How can we help? *</label>
                    <textarea
                      value={contactForm.message}
                      onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                      placeholder="Tell us about your practice and goals..."
                      required
                      rows={4}
                      className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none"
                      data-testid="contact-message"
                    />
                  </div>
                  
                  <Button 
                    type="submit"
                    className="w-full h-12 bg-primary text-white hover:bg-primary/90 rounded-full text-base"
                    disabled={submitting}
                    data-testid="contact-submit"
                  >
                    {submitting ? (
                      <span className="flex items-center gap-2">
                        <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                        Sending...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        Send Message
                        <Send className="w-4 h-4" />
                      </span>
                    )}
                  </Button>
                </div>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Plus Jakarta Sans' }}>
            Ready to Transform Your Practice?
          </h2>
          <p className="text-lg text-slate-600 mb-8 max-w-2xl mx-auto">
            Join the network of physicians who are building their practices through trusted relationships, not random ads.
          </p>
          <Button 
            size="lg"
            onClick={() => navigate("/register")}
            className="bg-primary text-white hover:bg-primary/90 rounded-full px-10 text-lg"
            data-testid="footer-cta-btn"
          >
            Join iConcierge Today
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 border-t border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            {/* Brand */}
            <div className="md:col-span-2">
              <div className="flex items-center gap-2 mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_medical-referral-hub/artifacts/m1ei4qxx_IMG_5363.jpeg" 
                  alt="iConcierge Logo" 
                  className="h-12 w-auto"
                />
              </div>
              <p className="text-slate-600 mb-4 max-w-md">
                Healthcare Marketing that builds your practice through trusted physician relationships and quality patient referrals.
              </p>
              <div className="flex items-center gap-2 text-slate-500 text-sm">
                <Shield className="w-4 h-4" />
                HIPAA-Compliant • Secure • Trusted
              </div>
            </div>

            {/* Contact Info */}
            <div>
              <h4 className="font-semibold text-slate-900 mb-4">Contact Us</h4>
              <div className="space-y-3">
                <a href="tel:8882509421" className="flex items-center gap-2 text-slate-600 hover:text-primary transition-colors">
                  <Phone className="w-4 h-4" />
                  (888) 250-9421
                </a>
                <a href="mailto:contact@iconciergehm.com" className="flex items-center gap-2 text-slate-600 hover:text-primary transition-colors">
                  <Mail className="w-4 h-4" />
                  contact@iconciergehm.com
                </a>
              </div>
              
              {/* Social Media Links */}
              <div className="flex items-center gap-3 mt-4">
                <a 
                  href="https://www.linkedin.com/company/i%E2%80%A2concierge-healthcare-marketing/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-slate-100 hover:bg-primary hover:text-white rounded-full flex items-center justify-center text-slate-600 transition-all"
                  data-testid="social-linkedin"
                >
                  <Linkedin className="w-5 h-5" />
                </a>
                <a 
                  href="https://facebook.com/iconciergehm" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-slate-100 hover:bg-primary hover:text-white rounded-full flex items-center justify-center text-slate-600 transition-all"
                  data-testid="social-facebook"
                >
                  <Facebook className="w-5 h-5" />
                </a>
                <a 
                  href="https://instagram.com/iconciergehm" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-slate-100 hover:bg-primary hover:text-white rounded-full flex items-center justify-center text-slate-600 transition-all"
                  data-testid="social-instagram"
                >
                  <Instagram className="w-5 h-5" />
                </a>
              </div>
            </div>

            {/* Address */}
            <div>
              <h4 className="font-semibold text-slate-900 mb-4">Visit Us</h4>
              <div className="flex items-start gap-2 text-slate-600">
                <MapPin className="w-4 h-4 mt-1 flex-shrink-0" />
                <div>
                  <p>2931 Ridge Rd, Ste 101</p>
                  <p>Rockwall, TX 75032</p>
                  <p>United States</p>
                </div>
              </div>
            </div>
          </div>

          <div className="pt-8 border-t border-slate-200 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-slate-500 text-sm">
              © 2025 iConcierge Healthcare Marketing. All rights reserved.
            </p>
            <div className="flex items-center gap-6 text-sm text-slate-500">
              <a href="#" className="hover:text-primary transition-colors">Privacy Policy</a>
              <a href="#" className="hover:text-primary transition-colors">Terms of Service</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
